import './HArticleList.scss';

import * as tsx from 'vue-tsx-support';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { HSpecialPageViewRef } from '../../HSpecialPageView';
import { SpecialPageModuleFilter } from '../../composables/module-filter/filter';
import { HArticleListItem } from './HArticleListItem';
import { ResolvedLanugageData } from '~/server-middleware/data-server/adapter/ACCOAdapter/schemes';
import { AvailableLanguage, SearchTagCategoryForGF } from '~/schemes';
import { ArticleListModule } from '~/server-middleware/data-server/adapter/ACCOAdapter/@types';
import { HIcon } from '~/components/HIcon';
import { HExpandTransition } from '~/components/transitions';
import { HCheckboxGroup } from '~/components/HForm';
import { HBtn } from '~/components/HBtn';

type ResolvedArticleListModule = ResolvedLanugageData<
  ArticleListModule['properties'],
  AvailableLanguage
>;

export interface HArticleListProps {
  value: ResolvedArticleListModule;
}

export interface HArticleListEmits {}

export interface HArticleListScopedSlots {}

@Component<HArticleListRef>({
  name: 'HArticleList',
  inject: ['specialPageViewRoot'],
  created() {
    this.filter = new SpecialPageModuleFilter(
      {
        properties: this.value,
        childKey: 'articles',
        searchTagCategories: this.searchTagCategories,
      },
      this,
    );
  },
  render() {
    const { categories, selectedTags, filteredChildren } = this;

    return (
      <div staticClass="v-article-list-module h-container">
        {/* 絞り込み条件 */}
        {!!categories.length && (
          <section staticClass="v-article-list-module-filter">
            <div staticClass="v-article-list-module-filter__control">
              <button
                staticClass="v-article-list-module-filter__activator"
                class={
                  this.isOpened &&
                  'v-article-list-module-filter__activator--active'
                }
                type="button"
                onClick={this.toggle}>
                <span staticClass="v-article-list-module-filter__activator__label">
                  {this.filterControlHeading}
                </span>
                <HIcon
                  staticClass="v-article-list-module-filter__activator__icon"
                  class={
                    this.isOpened &&
                    'v-article-list-module-filter__activator--icon-active'
                  }
                  name="keyboard-arrow-down"
                />
              </button>
            </div>
            <HExpandTransition>
              <div v-show={this.isOpened}>
                <div staticClass="v-article-list-module-filter__search-items">
                  {/* 絞り込みチェックボックス */}
                  {categories.map((category) => {
                    return (
                      <div>
                        <legend staticClass="v-article-list-module-filter__search-items__label">
                          {category.name}
                        </legend>
                        <HCheckboxGroup
                          key={category._id}
                          v-model={category.selectedSlugs}
                          items={category.tags.map(({ label, slug }) => {
                            return {
                              label,
                              value: slug,
                            };
                          })}
                          size="sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </HExpandTransition>
            {this.isOpened && (
              <div>
                {/* 絞り込みタグ表示エリア */}
                <div>
                  <h5
                    staticClass="v-article-list-module-filter-title"
                    class={
                      !selectedTags.length &&
                      'v-article-list-module-filter-title--disabled'
                    }>
                    {this.$t('label.filters')}
                  </h5>
                  <div staticClass="v-article-list-module-filter-search-condition">
                    <div staticClass="v-article-list-module-filter-search-condition__tags">
                      {this.categories.flatMap((category) => {
                        return category.selectedTags.map(
                          ({ _id, slug, label }) => {
                            return (
                              <HBtn
                                key={_id}
                                staticClass="v-article-list-module-filter-search-condition__tag"
                                size="sm"
                                onClick={() =>
                                  (category.selectedSlugs = category.selectedSlugs.filter(
                                    (selectedSlug) => selectedSlug !== slug,
                                  ))
                                }>
                                {label}
                                <HIcon name="close-circle" right />
                              </HBtn>
                            );
                          },
                        );
                      })}
                    </div>
                    <HBtn
                      staticClass="v-article-list-module-filter-search-condition__clear"
                      size="sm"
                      disabled={!selectedTags.length}
                      onClick={() => this.filter.clear()}>
                      Clear All
                      <HIcon name="close-circle" right />
                    </HBtn>
                  </div>
                </div>
                {/* 検索結果数表示エリア */}
                <p staticClass="v-article-list-module-filter-result">
                  {this.$t('label.searchResult')}：
                  <span staticClass="v-article-list-module-filter-result__count">
                    {filteredChildren.length}
                  </span>
                </p>
              </div>
            )}
          </section>
        )}
        <div staticClass="v-article-list-module__children">
          {filteredChildren.map((child) => (
            <HArticleListItem
              staticClass="v-article-list-module__child"
              value={child.properties}
              key={child.key}
            />
          ))}
        </div>
      </div>
    );
  },
})
export class HArticleListRef extends Vue implements HArticleListProps {
  readonly specialPageViewRoot?: HSpecialPageViewRef;
  @Prop({ type: Object, required: true })
  readonly value!: ResolvedArticleListModule;

  /**
   * 絞り込みチェクボックスを表示/非表示を制御する
   */
  private isOpened: boolean = false;

  /** 絞り込み全般処理 */
  private filter!: SpecialPageModuleFilter<
    ResolvedArticleListModule,
    'articles'
  >;

  /** 見出し */
  get filterControlHeading() {
    return this.filter.controlHeading;
  }

  /** 絞り込みチェクボックスに表示する検索カテゴリリスト */
  get categories() {
    return this.filter.categories;
  }

  /** 選択された検索タグリスト */
  get selectedTags() {
    return this.categories.flatMap((category) =>
      category.selectedTags.map((tag) => tag.label),
    );
  }

  /** 画面表示対象の子モジュール(記事) */
  get filteredChildren() {
    return this.filter.filteredChildren;
  }

  /** 検索タグカテゴリマスタ */
  get searchTagCategories(): SearchTagCategoryForGF[] {
    return this.specialPageViewRoot
      ? this.specialPageViewRoot.searchTagCategories
      : [];
  }

  /** 絞り込みチェクボックスの表示切り替え */
  toggle() {
    this.isOpened = !this.isOpened;
  }
}

export const HArticleList = tsx
  .ofType<HArticleListProps, HArticleListEmits, HArticleListScopedSlots>()
  .convert(HArticleListRef);
